import type { Nullish } from '../../types/utility';

const NON_NUMERIC_CHARS = /[\D]/gi;

type Output = '(000) 000-0000' | 'E.164';

/**
 * Format a US phone number
 *
 * @param number A 10-digit phone number with optional country code (e.g. 5558675309, 15558675309, +15558675309)
 * @returns The provided phone number in the format of (000) 000-0000 without the country code or +10000000000
 */
export function formatPhone(number: string | Nullish, output: Output = '(000) 000-0000') {
  if (typeof number !== 'string') {
    return '';
  }

  let formatted = number.replace(NON_NUMERIC_CHARS, '');
  const hasCountryCode = formatted.startsWith('1');

  if (output === 'E.164') {
    // re-add + since it's stripped above
    if (!hasCountryCode) {
      formatted = `1${formatted}`;
    }

    return `+${formatted}`;
  }

  formatted = hasCountryCode ? formatted.substring(1) : formatted;

  if (formatted.length >= 6) {
    const areaCode = formatted.substring(0, 3);
    const prefix = formatted.substring(3, 6);
    const suffix = formatted.substring(6, 10);

    return `(${areaCode}) ${prefix}-${suffix}`;
  } else if (formatted.length >= 3) {
    const areaCode = formatted.substring(0, 3);
    const prefix = formatted.substring(3);

    return `(${areaCode}) ${prefix}`;
  } else if (formatted.length > 0) {
    return `(${formatted}`;
  }

  return formatted;
}

/**
 * Format a US phone number to the E.164 format
 *
 * @deprecated Use formatPhone(number, 'E.164');
 *
 * @param number A phone number
 * @returns The provided phone number in the E.164 format (i.e. +15558675309)
 */
export function formatPhoneToE164(number: string) {
  let formatted = number.replace(NON_NUMERIC_CHARS, '');

  // re-add + since it's stripped above
  if (!formatted.startsWith('1')) {
    formatted = `1${formatted}`;
  }

  return `+${formatted}`;
}
